import type { CatalogFacets, TagFacet } from "@gooner/shared";
import { FACET_ORDER, facetOf } from "@gooner/shared";

/** The sidebar's non-facet section key (the "Other" keyword list). */
export const OTHER_SECTION = "other";

/**
 * Which /browse sidebar groups the URL says should be open.
 *
 * A group with an active param in the URL (`?race=latina`, `?act=…`, `?genre=…`) is
 * open, so a deep link or a keyword pill lands on a sidebar that shows the filter it
 * set instead of eleven closed labels with the answer folded inside one of them. A
 * legacy `?tag=race:latina` opens Race too (its facet has a group); any other `?tag=`
 * opens "Other", the list that keyword lives in.
 *
 * Only the facets that have a sidebar group (FACET_ORDER) count — a param for a facet
 * the sidebar doesn't render would name a group that isn't there.
 */
export function urlOpenGroups(facets: CatalogFacets, tag: string | null): string[] {
  const out: string[] = FACET_ORDER.filter((f) => facets[f]);
  if (tag) {
    const f = facetOf(tag);
    const key = f && FACET_ORDER.includes(f as TagFacet) ? f : OTHER_SECTION;
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

/**
 * The open set after the URL changed from `prevUrl` to `nextUrl` groups.
 *
 * Only groups that NEWLY gained a param open. Groups the visitor opened or closed by
 * hand keep their state: closing Race while ?race=latina is active and then picking a
 * Sex value does not spring Race back open, and clearing a filter never closes the
 * group it was in (the visitor is looking at it). URL-derived, but only on change.
 */
export function mergeUrlOpen(
  open: readonly string[],
  prevUrl: readonly string[],
  nextUrl: readonly string[],
): readonly string[] {
  const added = nextUrl.filter((k) => !prevUrl.includes(k) && !open.includes(k));
  return added.length ? [...open, ...added] : open;
}
